
export const EMPTY_OVERALL_POSITION =
  Object.freeze({
    expectedAnnualLoss: null,
    expectedAnnualLossFormatted: null,

    capitalAtRiskP95: null,
    capitalAtRiskP95Formatted: null,

    decisionsRequiringAttention: null,

    capitalRequired: null,
    capitalRequiredFormatted: null,

    residualRisk: null,
    residualRiskFormatted: null,

    coverageRatio: null,
    coverageRatioFormatted: null,

    scenariosWithProtectionGaps: null,
    riskAppetitePosition: null,

    activeScenarios: null,
    coveredScenarios: null,
    uncoveredScenarios: null,

    activeCoverages: null,
    expiringCoverages: null,
  });


export const EMPTY_ENGINE_STATE =
  Object.freeze({
    status: "idle",
    data: null,
    error: null,
    lastUpdated: null,
  });


function createEngineState() {
  return {
    ...EMPTY_ENGINE_STATE,
  };
}


function resolveNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value))
  ) {
    return null;
  }

  return Number(value);
}


function normalizeCurrency(
  currency
) {
  const normalized =
    String(currency || "")
      .trim()
      .toUpperCase();

  return /^[A-Z]{3}$/.test(normalized)
    ? normalized
    : "EUR";
}


function formatCurrency(
  value,
  currency
) {
  const number =
    resolveNumber(value);

  if (number === null) {
    return null;
  }

  try {
    return new Intl.NumberFormat(
      "en-IE",
      {
        style: "currency",
        currency:
          normalizeCurrency(currency),
        maximumFractionDigits: 0,
      }
    ).format(number);
  } catch {
    return new Intl.NumberFormat(
      "en-IE",
      {
        maximumFractionDigits: 0,
      }
    ).format(number);
  }
}


function formatPercentage(
  value
) {
  const number =
    resolveNumber(value);

  if (number === null) {
    return null;
  }

  /*
   * Risk Financing currently provides
   * transferred_pct as a value such as 79.2,
   * rather than 0.792.
   */
  return `${number.toLocaleString(
    "en-IE",
    {
      maximumFractionDigits: 1,
    }
  )}%`;
}


function resolveCurrency({
  currency,
  engines,
}) {
  return normalizeCurrency(
    currency ||

    engines?.financing
      ?.data
      ?.metadata
      ?.currency ||

    engines?.financing
      ?.data
      ?.portfolio
      ?.data
      ?.currency ||

    engines?.financing
      ?.data
      ?.portfolio
      ?.data
      ?.policy
      ?.currency
  );
}


export function createDecisionCenterContext({
  analytics = null,
  overallPosition = {},
  recommendations = [],
  decisions = [],
  decisionOutcomes = [],
  engines = {},
  executiveDecision = null,
  valueRealizationContext = null,
  currency = null,
} = {}) {
  const resolvedCurrency =
    resolveCurrency({
      currency,
      engines,
    });

  const expectedAnnualLoss =
    resolveNumber(
      overallPosition
        ?.expectedAnnualLoss
    );

  const capitalAtRiskP95 =
    resolveNumber(
      overallPosition
        ?.capitalAtRiskP95
    );

  const capitalRequired =
    resolveNumber(
      overallPosition
        ?.capitalRequired
    );

  const decisionsRequiringAttention =
    resolveNumber(
      overallPosition
        ?.decisionsRequiringAttention
    );

  const residualRisk =
    resolveNumber(
      overallPosition
        ?.residualRisk
    );

  const coverageRatio =
    resolveNumber(
      overallPosition
        ?.coverageRatio
    );

    
  const normalizedOverallPosition = {
    ...EMPTY_OVERALL_POSITION,
    ...overallPosition,

    expectedAnnualLoss,

    expectedAnnualLossFormatted:
      overallPosition
        ?.expectedAnnualLossFormatted ||
      formatCurrency(
        expectedAnnualLoss,
        resolvedCurrency
      ),

    capitalAtRiskP95,

    capitalAtRiskP95Formatted:
      overallPosition
        ?.capitalAtRiskP95Formatted ||
      formatCurrency(
        capitalAtRiskP95,
        resolvedCurrency
      ),

    decisionsRequiringAttention,

    capitalRequired,

    capitalRequiredFormatted:
      overallPosition
        ?.capitalRequiredFormatted ||
      formatCurrency(
        capitalRequired,
        resolvedCurrency
      ),


    residualRisk,

    residualRiskFormatted:
      overallPosition
        ?.residualRiskFormatted ||
      formatCurrency(
        residualRisk,
        resolvedCurrency
      ),

    coverageRatio,

    coverageRatioFormatted:
      overallPosition
        ?.coverageRatioFormatted ||
      formatPercentage(
        coverageRatio
      ),
  };

  const normalizedRecommendations =
    Array.isArray(recommendations)
      ? recommendations
      : [];

  const normalizedDecisions =
    Array.isArray(decisions)
      ? decisions
      : [];

  const normalizedDecisionOutcomes = Array.isArray(decisionOutcomes)
    ? decisionOutcomes
    : [];

  const recommendationCountByEngine = normalizedRecommendations.reduce(
    (counts, recommendation) => ({
      ...counts,
      [recommendation.engine]: (counts[recommendation.engine] || 0) + 1,
    }),
    {}
  );

  return {
    analytics,
    valueRealizationContext,
    executiveDecision,
    overallPosition:
      normalizedOverallPosition,

    recommendations:
      normalizedRecommendations,

    decisions:
      normalizedDecisions,

    decisionOutcomes:
      normalizedDecisionOutcomes,

    engines: {
      portfolio: {
        ...createEngineState(),
        ...(engines.portfolio || {}),
      },

      scenario: {
        ...createEngineState(),
        ...(engines.scenario || {}),
      },

      financing: {
        ...createEngineState(),
        ...(engines.financing || {}),
      },

      governance: {
        ...createEngineState(),
        ...(engines.governance || {}),
      },
    },

    metadata: {
      generatedAt:
        new Date().toISOString(),

      recommendationCount:
        normalizedRecommendations.length,

      recommendationCountByEngine,

      uniqueRecommendationIds: normalizedRecommendations
        .filter(({ id }) => id != null)
        .map(({ id, engine }) => `${engine}:${id}`),

      decisionCount:
        normalizedDecisions.length,

      currency:
        resolvedCurrency,
    },
  };
}
